/* ================= FIELD LABELS ================= */

const FIELD_LABEL_MAP = {
  teststep: 'Test step',
  attachment: 'Attachment',
  is_automation_needed: 'Candidate for automation',
  not_automated_category: 'Category (not automated)',
  not_automated_sub_category: 'Subcategory (not automated)',
};

const normalizeFieldKey = (field = '') =>
  field.toLowerCase().replace(/\s+/g, '').replace(/_+/g, '_');

/* ================= HELPERS ================= */

export const getUserName = (u) =>
  u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : 'Unknown';

export const getRelativeTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

/* ================= FIELD FORMAT ================= */

export const formatFieldName = (field = '') => {
  const normalized = normalizeFieldKey(field);

  if (FIELD_LABEL_MAP[normalized]) {
    return FIELD_LABEL_MAP[normalized];
  }

  return field
    .replace(/teststep[-_]?/gi, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
};

/* ================= VALUE FORMAT ================= */

export const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';

  if (typeof v === 'string' && v.includes('@')) {
    const parts = v.split('@');
    const fileName = parts[parts.length - 1];
    if (fileName?.includes('.')) return fileName;
  }

  return String(v);
};


export const processEntries = (entries) => {
  const map = new Map();

  entries.forEach((entry) => {
    const date = new Date(entry.created_at);
    const minuteKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;

    const key = `${entry.performed_by_user?.first_name}-${minuteKey}-${entry.action_type}`;

    const changes = Object.entries(entry.changes || {})
      .map(([field, { old_value, new_value }]) => {
        const oldVal = formatValue(old_value);
        const newVal = formatValue(new_value);

        if (oldVal === newVal) return null;
        if (!oldVal && !newVal) return null;

        const normalized = normalizeFieldKey(field);

        return {
          field: formatFieldName(field),
          rawField: normalized,
          group: normalized.includes('not_automated')
            ? 'automation_reason'
            : 'general',
          old: oldVal,
          new: newVal,
        };
      })
      .filter(Boolean);

    if (!changes.length && entry.action_type === 'UPDATE') return;

    if (!map.has(key)) {
      map.set(key, { ...entry, changes });
    } else {
      map.get(key).changes.push(...changes);
    }
  });

  return Array.from(map.values());
};

/* ================= SUMMARY ================= */

export const buildSummary = (entry) => {
  if (entry.action_type === 'CREATE') return 'created this test case';
  if (entry.action_type === 'DELETE') return 'removed';

  if (!entry.changes.length) return 'made updates';

  const isAutomationCandidate = entry.changes.some(
    (c) => c.rawField === 'is_automation_needed'
  );

  if (isAutomationCandidate) {
    return 'marked as candidate for automation';
  }

  const hasAutomationReason = entry.changes.some(
    (c) => c.group === 'automation_reason'
  );

  if (hasAutomationReason) {
    return 'updated reason for not automating';
  }
  const hasAdded = entry.changes.some((c) => !c.old && c.new);

  const fields = [...new Set(entry.changes.map((c) => c.field.toLowerCase()))];

  if (hasAdded) {
    return fields.length <= 3
      ? `added ${fields.join(', ')}`
      : `added ${fields.length} fields`;
  }

  return fields.length <= 3
    ? `updated ${fields.join(', ')}`
    : `updated ${fields.length} fields`;
};